import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Paper, Alert, CircularProgress, Checkbox, Divider, Grid
} from '@mui/material';
import { HowToReg, PersonAdd } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const api = {
  get:  (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  post: (url, data, cfg={}) => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
  put:  (url, data, cfg={}) => axios.put(`${process.env.REACT_APP_API_URL}${url}`, data, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug'), ...cfg.headers }}),
};

export default function CourseRegistration() {
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses]       = useState([]);
  const [years, setYears]           = useState([]);
  const [students, setStudents]     = useState([]);
  const [registered, setRegistered] = useState([]);
  const [selectedCourse, setCourse] = useState('');
  const [selectedYear, setYear]     = useState('');
  const [semester, setSemester]     = useState(1);
  const [selected, setSelected]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    api.get('/tertiary/courses').then(r => setCourses(r.data.data || []));
    api.get('/academic-years').then(r => {
      const yrs = r.data.data || [];
      setYears(yrs);
      if (yrs.length > 0) setYear(yrs[0].id);
    });
    api.get('/students').then(r => setStudents(r.data.data || []));
  }, []);

  const load = async () => {
    if (!selectedCourse || !selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/tertiary/course-registrations', {
        params: { course_id: selectedCourse, academic_year_id: selectedYear, semester }
      });
      setRegistered(r.data.data || []);
      setSelected([]);
    } catch { enqueueSnackbar('Failed to load registrations', { variant: 'error' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selectedCourse, selectedYear, semester]);

  const registeredIds = new Set(registered.map(r => r.student_id));
  const unregistered  = students.filter(s => !registeredIds.has(s.id));

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(selected.length === unregistered.length ? [] : unregistered.map(s => s.id));
  };

  const register = async () => {
    if (!selected.length) return enqueueSnackbar('Select at least one student', { variant: 'warning' });
    setSaving(true);
    let success = 0;
    for (const student_id of selected) {
      try {
        await api.post('/tertiary/course-registrations', {
          student_id, course_id: selectedCourse, academic_year_id: selectedYear, semester
        });
        success++;
      } catch (e) {
        if (e.response?.status !== 409) enqueueSnackbar(`Failed for student ${student_id}`, { variant: 'error' });
      }
    }
    enqueueSnackbar(`${success} student(s) registered`, { variant: 'success' });
    setSaving(false);
    load();
  };

  const drop = async (regId) => {
    if (!window.confirm('Drop this student from the course?')) return;
    try {
      await api.put(`/tertiary/course-registrations/${regId}/drop`, {});
      enqueueSnackbar('Student dropped', { variant: 'info' });
      load();
    } catch { enqueueSnackbar('Failed to drop student', { variant: 'error' }); }
  };

  const course = courses.find(c => String(c.id) === String(selectedCourse));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Course Registration</Typography>
          <Typography variant="body2" color="text.secondary">Enrol students into courses for a given year and semester</Typography>
        </Box>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <TextField select size="small" label="Course" value={selectedCourse}
          onChange={e => setCourse(e.target.value)} sx={{ minWidth: 280 }}>
          <MenuItem value="">Select Course</MenuItem>
          {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Academic Year" value={selectedYear}
          onChange={e => setYear(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Select Year</MenuItem>
          {years.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Semester" value={semester}
          onChange={e => setSemester(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value={1}>Semester 1</MenuItem>
          <MenuItem value={2}>Semester 2</MenuItem>
        </TextField>
      </Stack>

      {course && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>{course.code} — {course.name}</strong> &nbsp;|&nbsp;
          Credits: {course.credits} &nbsp;|&nbsp;
          CA: {course.ca_weight || 40}% · Exam: {course.exam_weight || 60}%
          &nbsp;|&nbsp; Registered: <strong>{registered.filter(r => r.status !== 'dropped').length}</strong> students
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Currently registered */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#0f2b4a', borderRadius: '8px 8px 0 0' }}>
              <Typography fontWeight={700} color="white" fontSize={14}>
                Registered Students ({registered.filter(r => r.status !== 'dropped').length})
              </Typography>
            </Box>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : registered.filter(r => r.status !== 'dropped').length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography fontSize={13}>No students registered yet</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#f8f9fc' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                  <TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {registered.filter(r => r.status !== 'dropped').map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500}>{r.last_name} {r.first_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{r.admission_number}</Typography>
                      </TableCell>
                      <TableCell><Chip label={r.status} size="small" color="success" /></TableCell>
                      <TableCell>
                        <Button size="small" color="error" onClick={() => drop(r.id)}>Drop</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>

        {/* Available to register */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#2e7d32', borderRadius: '8px 8px 0 0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={700} color="white" fontSize={14}>
                  Available Students ({unregistered.length})
                </Typography>
                <Button size="small" variant="outlined" onClick={selectAll}
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                  {selected.length === unregistered.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Stack>
            </Box>
            {unregistered.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography fontSize={13}>All students are registered</Typography>
              </Box>
            ) : (
              <>
                <Table size="small">
                  <TableBody>
                    {unregistered.map(s => (
                      <TableRow key={s.id} hover onClick={() => toggleSelect(s.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={500}>{s.last_name} {s.first_name}</Typography>
                          <Typography fontSize={11} color="text.secondary">{s.admission_number}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
                  <Button fullWidth variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <HowToReg />}
                    onClick={register} disabled={saving || !selected.length || !selectedCourse || !selectedYear}
                    sx={{ bgcolor: '#0f2b4a' }}>
                    {saving ? 'Registering...' : `Register ${selected.length || ''} Selected Student${selected.length !== 1 ? 's' : ''}`}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
